import { yupResolver } from "@hookform/resolvers/yup";
import { ChevronLeftIcon, EnterIcon, TrashIcon } from "@radix-ui/react-icons";
import { Badge, Button, Card, Flex, Separator, Spinner, Tabs } from "@radix-ui/themes";
import moment from "moment";
import { useState } from "react";
import { momentLocalizer } from "react-big-calendar";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useMutation, useQueryClient } from "react-query";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ConfirmAlertDialog } from "../../components/common/ConfirmAlertDialog";
import { Lists } from "../../components/common/Lists";
import { SchoolSchedule } from "../../components/common/SchoolSchedule";
import TableComponent from "../../components/common/Tables";
import SelectComponent from "../../components/inputs/Select/SelectComponent";
import TimeFieldComponent from "../../components/inputs/Timefield/TimefieldComponent";
import { useAppDispatch } from "../../hooks/hooks";
import { useGetAssignedTeachersClassesSubjectsQuery } from "../../hooks/useGetAssignedTeachersClassesSubjectsQuery";
import { useGetClassroomQuery } from "../../hooks/useGetClassroomQuery";
import { useGetCoursesSubjectsQuery } from "../../hooks/useGetCoursesSubjectsQuery";
import { useGetSchoolSchedulesQuery } from "../../hooks/useGetSchoolSchedulesQuery";
import { resetClassData } from "../../redux/slices/school/classes";
import { RootState } from "../../redux/store";
import { routes } from "../../routes";
import { createSchoolSchedule, updateSchoolSchedule } from "../../services/school/schoolschedule/schoolschedule";
import { SchoolScheduleType } from "../../types/SchoolSchedule";
import { Key_schoolschedules } from "../../utils/constants";
import { formatTime } from "../../utils/dateFormatter";
import { days, daysTranslation } from "../../utils/weekdays";
import { CreateSchoolScheduleFormValidation } from "./CreateSchoolScheduleFormValidation";
import { useGetPlanSubjectsQuery } from "../../hooks/useGetPlanSubjectsQuery";
import { AlertDialog } from '@radix-ui/themes';
const columnNamesMap: { [key: string]: string } = {
    subject: 'Disciplina',
    teacher: 'Docente',
    classroom: 'Sala',
    startTime: 'Hora de Início',
    endTime: 'Hora de Término',
};

type ActiveSegmentTab = 'Form' | 'Preview';

export const ManageSchedule = () => {
    const [activeSegment, setActiveSegment] = useState<ActiveSegmentTab>('Form');
    const [currentPage, setCurrentPage] = useState(1);
    const [deleteItem, setDeleteItem] = useState<SchoolScheduleType | null>(null);
    const queryClient = useQueryClient();
    const [showChangesNotification, setShowChangesNotification] = useState(false);
    const [conflictKey, setConflictKey] = useState(0);
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const mutationUpdate = useMutation(updateSchoolSchedule, {
        onSuccess: () => {
            queryClient.invalidateQueries(Key_schoolschedules);
            toast.success('Registo eliminado');
        },
        onError: () => {
            toast.error("Erro ao eliminar o registo.")
        }
    });
    const handleDelete = (ss: SchoolScheduleType) => {
        ss.status = false;
        mutationUpdate.mutateAsync(ss)
    };
    const openDeleteDialog = (s: SchoolScheduleType) => {
        setDeleteItem(s);
    };
    const confirmDelete = async () => {
        if (deleteItem) {
            await handleDelete(deleteItem!)
        }
    };
    const mutation = useMutation({ mutationFn: createSchoolSchedule, mutationKey: 'createSchoolSchedule' });
    const classData = useSelector((state: RootState) => state.classes);
    const { idCourse, level, semester, idDepartament, id, description, course } = classData;
    const { data: classrooms, isLoading: isLoadingClassrooms, isError: isErrorClassrooms } = useGetClassroomQuery({ PageNumber: 1, PageSize: 400, IdDepartament: idDepartament }, { enabled: !!idDepartament });
    const { data: schoolSchedules, status: statusSchoolSchedule, isLoading: isLoadingSchoolSchedules, isError: isErrorSchoolSchedules, refetch: refreshSchoolSchedule } = useGetSchoolSchedulesQuery({ PageNumber: 1, PageSize: 400, IdClass: id }, { enabled: !!id });
    const [conflictMessage, setConflictMessage] = useState<string | null>(null);
    const { data: subjects, isLoading: isLoadingSubjects, isError: isErrorSubjects, refetch: refreshCourseSubjects } = useGetPlanSubjectsQuery({
        PageSize: 400,
        PageNumber: 1,
        Level: level,
        Semester: semester,
        IdCourse: idCourse,
    }, { enabled: !!level && !!semester && !!idCourse });
    const defaultValues: SchoolScheduleType = {
        idClassroom: "",
        dayOfTheWeek: "",
        idClass: classData.id!,
        startTime: "",
        endTime: "",
        idTeacher: "",
        idSubject: "",
        subject: ""
    }
    if (id) {
        defaultValues.idClass = id
    }
    const { control, handleSubmit, setValue, getValues, formState: { errors }, watch, trigger, reset } = useForm<SchoolScheduleType>({
        resolver: yupResolver(CreateSchoolScheduleFormValidation),
        defaultValues,
        mode: 'onBlur',
        reValidateMode: 'onBlur',
        shouldUseNativeValidation: false,
    });
    const { data: teachersClassesSubjectsData, status, isLoading: isLoadingTeachersClassesSubjectsData, refetch: refreshAssignedTeachersClassesSubjects } = useGetAssignedTeachersClassesSubjectsQuery({
        PageSize: 400,
        PageNumber: 1,
        IdClass: id,
        IdSubject: getValues('idSubject')
    }, { enabled: !!getValues('idSubject') && !!id });

    const handleBack = () => {
        navigate(routes.classes);
        dispatch(resetClassData());
    }
    const onSubmit = handleSubmit(async (data) => {
        try {

            const startTime = formatTime(data.startTime);
            const endTime = formatTime(data.endTime);

            const newStart = moment(startTime, "HH:mm");
            const newEnd = moment(endTime, "HH:mm");


            let conflict: string | null = null;


            const hasConflict = schoolSchedules?.items.some((s) => {
                if (s.dayOfTheWeek !== data.dayOfTheWeek) return false;

                const existingStart = moment(s.startTime, "HH:mm");
                const existingEnd = moment(s.endTime, "HH:mm");


                const sameRoomConflict =
                    s.idClassroom === data.idClassroom &&
                    newStart.isBefore(existingEnd) &&
                    newEnd.isAfter(existingStart);


                const teacherConflict =
                    s.idTeacher === data.idTeacher &&
                    newStart.isBefore(existingEnd) &&
                    newEnd.isAfter(existingStart);

                if (sameRoomConflict) {
                    conflict = "Já existe uma aula registada nesta sala para o mesmo horário.";
                    return true;
                }

                if (teacherConflict) {
                    conflict = "O docente já está alocado a outra turma neste horário.";
                    return true;
                }

                return false;
            });

            if (hasConflict && conflict) {
                setConflictMessage(conflict);
                setConflictKey(prev => prev + 1);
                setShowChangesNotification(true);
                return;
            }
            data.startTime = startTime;
            data.endTime = endTime;
            const result = await mutation.mutateAsync(data);
            if (result) {
                toast.success("Horário registado!");
                refreshSchoolSchedule();
                reset();
            }
        } catch (err: any) {
            toast.error(`${mutation.error ?? err?.message}`);
        }
    });
    const localizer = momentLocalizer(moment);
    const convertToCalendarEvents = () => {
        const currentDate = moment().startOf('week');

        return schoolSchedules?.items.map(schedule => {
            const dayIndex = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday',]
                .indexOf(schedule.dayOfTheWeek);
            schedule.description = `${schedule.subject} | ${schedule.teacher} | ${schedule.classroom}`;
            const [startHour, startMinute] = schedule.startTime.split(':').map(Number);
            const [endHour, endMinute] = schedule.endTime.split(':').map(Number);

            const startDate = moment(currentDate)
                .add(dayIndex, 'days')
                .set('hour', startHour)
                .set('minute', startMinute)
                .toDate();

            const endDate = moment(currentDate)
                .add(dayIndex, 'days')
                .set('hour', endHour)
                .set('minute', endMinute)
                .toDate();

            return {
                title: schedule.description || "Sem descrição",
                start: startDate,
                end: endDate,
                allDay: false,
            };
        });
    };


    const events = convertToCalendarEvents();
    const navigateAndValidate = (tabToNavigateTo: ActiveSegmentTab) => {
        if (activeSegment !== tabToNavigateTo) setActiveSegment(tabToNavigateTo);
        trigger();
    }
    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };
    const options = schoolSchedules?.items.map((ss) => [
        {
            label: 'Eliminar',
            onClick: () => openDeleteDialog(ss),
            icon: <TrashIcon />
        }

    ]) || [];
    const handleSubjectChange = (idSubject: string) => {
        setValue('idSubject', idSubject)
        refreshAssignedTeachersClassesSubjects();
    }
    return (
        <>
            <Flex align="center">
                <Badge variant="surface" >{course}</Badge>
                <Separator orientation="horizontal" />
                <Badge variant="outline" >{description}</Badge>
            </Flex>
            <Tabs.Root value={activeSegment} onValueChange={(tabToNavigate) => {
                navigateAndValidate(tabToNavigate as ActiveSegmentTab);
            }}>
                <Tabs.List>
                    <Tabs.Trigger value="Form">Configurar horário da turma  <Badge color="red">
                        {Object.keys(errors)?.length}
                    </Badge> </Tabs.Trigger>
                    <Tabs.Trigger value="Preview">Visualizar  </Tabs.Trigger>
                </Tabs.List>
                <Tabs.Content value="Form">
                    <Card>
                        <Flex direction="row" gap='6'>
                            <Flex direction="column" gap="5" style={{ flex: 1 }}>
                                <form onSubmit={onSubmit} noValidate>
                                    <Flex direction='column' gap='4'>
                                        <SelectComponent
                                            label="Disciplina"
                                            options={subjects?.items.map((subject) => ({
                                                value: subject.idSubject,
                                                label: subject.subject ?? ""
                                            }))}
                                            errors={errors}
                                            control={control}
                                            itemsError={isErrorSubjects}
                                            itemsLoading={isLoadingSubjects}
                                            name={"idSubject"}
                                            onSelect={handleSubjectChange}
                                            required={true}
                                        />
                                        <SelectComponent
                                            label="Docente"
                                            options={teachersClassesSubjectsData?.items.map((t) => ({
                                                value: t.idTeacher,
                                                label: `${t.teacher}`
                                            }))}
                                            errors={errors}
                                            control={control}
                                            itemsLoading={isLoadingTeachersClassesSubjectsData}
                                            name={"idTeacher"}
                                            required={true}
                                        />
                                        <SelectComponent
                                            label="Sala"
                                            options={classrooms?.items.map((c) => ({
                                                value: c.id!,
                                                label: c.description!
                                            }))}
                                            errors={errors}
                                            control={control}
                                            itemsError={isErrorClassrooms}
                                            itemsLoading={isLoadingClassrooms}
                                            name={"idClassroom"}
                                            required={true}
                                        />
                                        <SelectComponent
                                            label="Dias da semana"
                                            options={days.map((day) => ({
                                                value: day,
                                                label: daysTranslation[day as keyof typeof daysTranslation] || day
                                            }))}
                                            errors={errors}
                                            control={control}
                                            name={"dayOfTheWeek"}
                                            required={true}
                                        />
                                        <Flex gap='4'>
                                            <TimeFieldComponent
                                                name="startTime"
                                                label="Hora de Início"
                                                control={control}
                                                errors={errors}
                                                required={true}
                                            />
                                            <TimeFieldComponent
                                                name="endTime"
                                                label="Hora de Término"
                                                control={control}
                                                errors={errors}
                                                required={true}
                                            />
                                        </Flex>
                                        <Flex justify="end" gap='4'>
                                            <Button type='button' variant='soft' color='gray' onClick={handleBack} disabled={mutation.isLoading}>
                                                <ChevronLeftIcon /> Voltar
                                            </Button>
                                            <Button type="submit" disabled={mutation.isLoading} variant='surface'>
                                                {mutation.isLoading ? <Spinner loading> <EnterIcon /></Spinner> : <EnterIcon />}
                                                Registar
                                            </Button>
                                        </Flex>
                                    </Flex>
                                </form>
                            </Flex>
                            <Flex direction="column" gap="4" style={{ flex: 1 }}>
                                <Lists
                                    status={statusSchoolSchedule}
                                    TableComponent={() => <TableComponent
                                        columns={Object.keys(columnNamesMap)}
                                        items={schoolSchedules?.items}
                                        options={options}
                                        columnNamesMap={columnNamesMap}
                                        totalPages={schoolSchedules?.totalPages || 1}
                                        currentPage={currentPage}
                                        onPageChange={handlePageChange} />} handleClickCreate={undefined}
                                />
                            </Flex>
                        </Flex>
                    </Card>
                </Tabs.Content>
                <Tabs.Content value="Preview">
                    <div className="h-screen p-4">
                        <SchoolSchedule events={events} />
                    </div>
                </Tabs.Content>
            </Tabs.Root>

            <ConfirmAlertDialog
                open={!!deleteItem}
                onOpenChange={(open) => !open && setDeleteItem(null)}
                title="Eliminar registo do horário"
                description="Esta operação é irreversível."
                confirmLabel="Eliminar"
                item={deleteItem}
                onConfirm={confirmDelete}
                onCancel={() => setDeleteItem(null)}
            />
            {showChangesNotification && conflictMessage && (
                <AlertDialog.Root
                    key={conflictKey}
                    open={showChangesNotification}
                    onOpenChange={() => setShowChangesNotification(false)}
                >
                    <AlertDialog.Content maxWidth="600px">
                        <AlertDialog.Title>Conflito de horário</AlertDialog.Title>
                        <AlertDialog.Description>
                            {conflictMessage}
                        </AlertDialog.Description>
                        <Flex gap="3" justify="center" mt="4">
                            <AlertDialog.Cancel>
                                <Button type="button" variant="soft" color="gray">
                                    Entendido
                                </Button>
                            </AlertDialog.Cancel>
                        </Flex>
                    </AlertDialog.Content>
                </AlertDialog.Root>
            )}

        </>

    );
}
